import { createContext, createElement, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { buildSiteSettings, type SiteSettings } from "@/lib/siteSettings";


export type { SiteSettings } from "@/lib/siteSettings";

export const BRAND_COLORS: { name: string; value: string }[] = [
  { name: "Night Mesquite", value: "#2A1510" },
  { name: "Prairie Cream", value: "#F5EFE2" },
  { name: "Ranch Sunset", value: "#FA5021" },
  { name: "Saddle Tan", value: "#C6A971" },
  { name: "Creek Bed Brown", value: "#412019" },
  { name: "Worn Leather", value: "#8B6F47" },
  { name: "Ivory", value: "#FDFAF3" },
  { name: "Dust Bone", value: "#E8DCC4" },
  { name: "Sun-bleached", value: "#D4CCBC" },
  { name: "Pure White", value: "#FFFFFF" },
];

export const SITE_SETTINGS_QUERY_KEY = ["site_settings"] as const;
const SiteSettingsContext = createContext<SiteSettings | undefined>(undefined);

export function SiteSettingsProvider({ settings, children }: { settings: SiteSettings; children: ReactNode }) {
  return createElement(SiteSettingsContext.Provider, { value: settings }, children);
}

async function fetchSiteSettings(): Promise<SiteSettings> {
  const { data, error } = await supabase.from("site_settings").select("key, value");
  if (error) throw error;
  return buildSiteSettings(data);
}

export function useSiteSettings() {
  const initialSettings = useContext(SiteSettingsContext);
  return useQuery({
    queryKey: SITE_SETTINGS_QUERY_KEY,
    queryFn: fetchSiteSettings,
    initialData: initialSettings,
    staleTime: 30_000,
  });
}


export function isNavVisible(visibility: Record<string, boolean> | undefined, label: string): boolean {
  if (!visibility) return true;
  return visibility[label] !== false;
}
